import React from "react";
import AccordionEditable from "../../../../../components/ui/accordions/AccordionEditable";
import ContentCard from "../../../../../components/ui/cards/ContentCard";
import Tooltip from "../../../../../components/ui/Tooltip";
import { usaStates, puchasedAssetsType } from "../../../../../components/ui/forms/selectOptionData";
import DocumentList from "../../../../../components/ui/forms/DocumentList";

// Helper function to convert state code to full state name
const getStateName = (stateCode) => {
  if (!stateCode) return stateCode;
  const stateCodeStr = typeof stateCode === 'string' ? stateCode : String(stateCode);
  const state = usaStates().find((s) => s.value === stateCodeStr.toLowerCase());
  return state ? state.label : stateCodeStr;
};

// Helper function to convert asset type value to label
const getAssetTypeLabel = (assetTypeValue) => {
  if (!assetTypeValue) return assetTypeValue;
  const assetType = puchasedAssetsType().find((a) => a.value === assetTypeValue.toLowerCase());
  return assetType ? assetType.label : assetTypeValue;
};

const BTFTaxSituationSection = ({ situation, documents = [], isCpa, onOpenUploadModal, onDocumentDeleted, summaryMetrics, disableDelete = false }) => {
  if (!situation) return null;
  
  // Get metrics from API
  const metrics = summaryMetrics?.business_documents_btf || {};

  const taxYearsArray = Array.isArray(situation.taxYears) ? situation.taxYears : (situation.taxYears ? [situation.taxYears] : []);
  const taxYearsNumeric = taxYearsArray.map(year => Number(year));

  // Filter documents by type and year
  const getDocumentsByType = (documentType) => {
    return documents.filter(doc => {
      if (!doc.documentType) return false;
      const docTypeMatch = doc.documentType.toUpperCase() === documentType.toUpperCase();
      const docYear = doc.documentYear ? Number(doc.documentYear) : null;
      const yearMatch = docYear !== null && taxYearsNumeric.includes(docYear);
      return docTypeMatch && yearMatch;
    });
  };

  // Render Gain & Loss Card
  const renderGainLossCard = () => {
    const gainLossMetrics = metrics["gain_loss"];
    if (!gainLossMetrics) return null;

    const { required, provided, complete } = gainLossMetrics;
    const needsAttention = !complete;
    const gainLossDocuments = getDocumentsByType("GAIN_LOSS");

    return (
      <ContentCard key="gain-loss">
        <div className="section-header-row">
          {needsAttention && (
            <img 
              src={`${process.env.PUBLIC_URL}/images/icons/danger_not_filled.svg`} 
              alt="Attention needed" 
              className="attention-indicator-icon"
            />
          )}
          <h4 className="section-header-title">Gain & Loss</h4>
          {!isCpa && needsAttention && (
            <button
              type="button"
              className="section-header-upload"
              onClick={() => onOpenUploadModal({ name: "gainLoss", label: "Gain & Loss" })}
            >
              <img 
                src={`${process.env.PUBLIC_URL}/images/icons/upload_primary_blue.svg`} 
                alt="Upload" 
                className="section-header-upload-icon"
              />
              Upload
            </button>
          )}
        </div>
        <div className="section-meta-row">
          <span className="section-meta-item">
            <span className="section-meta-label">Required:</span>
            <span className="section-meta-value">{required}</span>
          </span>
          <span className="section-meta-item">
            <span className="section-meta-label">Submitted:</span>
            <span className="section-meta-value">{provided}</span>
          </span>
        </div>
        {gainLossDocuments.length > 0 && (
          <DocumentList documents={gainLossDocuments} onDocumentDeleted={onDocumentDeleted} isCpa={isCpa} disableDelete={disableDelete} />
        )}
      </ContentCard>
    );
  };

  // Render State Annual Report Card
  const renderStateAnnualReportCard = () => {
    if (!situation.stateAnnualReport?.value) return null;
    
    const stateAnnualReportMetrics = metrics["state_annual_report"];
    if (!stateAnnualReportMetrics) return null;

    const { required, provided, complete } = stateAnnualReportMetrics;
    const needsAttention = !complete;
    const stateAnnualReportDocuments = getDocumentsByType("STATE_ANNUAL_REPORT");
    
    const states = situation.stateAnnualReport?.fields?.stateAnnualReports || [];
    const statesLabel = states.length > 0
      ? states.map(state => getStateName(state)).join(", ")
      : null;

    // Build tooltip content for info (excluding action required message)
    const tooltipContent = statesLabel ? `States: ${statesLabel}` : null;

    return (
      <ContentCard key="state-annual-report">
        <div className="section-header-row">
          {needsAttention && (
            <img 
              src={`${process.env.PUBLIC_URL}/images/icons/danger_not_filled.svg`} 
              alt="Attention needed" 
              className="attention-indicator-icon"
            />
          )}
          <h4 className="section-header-title">State Annual Report</h4>
          {tooltipContent && (
            <Tooltip icon="info" content={tooltipContent} />
          )}
          {!isCpa && needsAttention && (
            <button
              type="button"
              className="section-header-upload"
              onClick={() => onOpenUploadModal({ name: "stateAnnualReport", label: "State Annual Report" })}
            >
              <img 
                src={`${process.env.PUBLIC_URL}/images/icons/upload_primary_blue.svg`} 
                alt="Upload" 
                className="section-header-upload-icon"
              />
              Upload
            </button>
          )}
        </div>
        <div className="section-meta-row">
          <span className="section-meta-item">
            <span className="section-meta-label">Required:</span>
            <span className="section-meta-value">{required}</span>
          </span>
          <span className="section-meta-item">
            <span className="section-meta-label">Submitted:</span>
            <span className="section-meta-value">{provided}</span>
          </span>
        </div>
        {stateAnnualReportDocuments.length > 0 && (
          <DocumentList documents={stateAnnualReportDocuments} onDocumentDeleted={onDocumentDeleted} isCpa={isCpa} disableDelete={disableDelete} />
        )}
      </ContentCard>
    );
  };

  // Render Purchase Assets Card
  const renderPurchaseAssetsCard = () => {
    if (!situation.purchaseAssets?.value) return null;
    
    const purchaseAssetsMetrics = metrics["purchase_assets"];
    if (!purchaseAssetsMetrics) return null;

    const { required, provided, complete } = purchaseAssetsMetrics;
    const needsAttention = !complete;
    const purchaseAssetsDocuments = getDocumentsByType("PURCHASE_ASSETS");
    
    const assetTypes = situation.purchaseAssets?.fields?.puchasedAssetsType || [];
    const assetTypesLabel = assetTypes.length > 0
      ? assetTypes.map(type => getAssetTypeLabel(type)).join(", ")
      : null;

    // Build tooltip content for info (excluding action required message)
    const tooltipContent = assetTypesLabel ? `Assets Type: ${assetTypesLabel}` : null;

    return (
      <ContentCard key="purchase-assets">
        <div className="section-header-row">
          {needsAttention && (
            <img 
              src={`${process.env.PUBLIC_URL}/images/icons/danger_not_filled.svg`} 
              alt="Attention needed" 
              className="attention-indicator-icon"
            />
          )}
          <h4 className="section-header-title">Purchase Assets</h4>
          {tooltipContent && (
            <Tooltip icon="info" content={tooltipContent} />
          )}
          {!isCpa && needsAttention && (
            <button
              type="button"
              className="section-header-upload"
              onClick={() => onOpenUploadModal({ name: "purchaseAssets", label: "Purchase Assets" })}
            >
              <img 
                src={`${process.env.PUBLIC_URL}/images/icons/upload_primary_blue.svg`} 
                alt="Upload" 
                className="section-header-upload-icon"
              />
              Upload
            </button>
          )}
        </div>
        <div className="section-meta-row">
          <span className="section-meta-item">
            <span className="section-meta-label">Required:</span>
            <span className="section-meta-value">{required}</span>
          </span>
          <span className="section-meta-item">
            <span className="section-meta-label">Submitted:</span>
            <span className="section-meta-value">{provided}</span>
          </span>
        </div>
        {purchaseAssetsDocuments.length > 0 && (
          <DocumentList documents={purchaseAssetsDocuments} onDocumentDeleted={onDocumentDeleted} isCpa={isCpa} disableDelete={disableDelete} />
        )}
      </ContentCard>
    );
  };

  // Use API metrics for accordion badges
  const sectionComplete = metrics.complete ?? false;
  const sectionNeedsAttention = metrics.attention_needed_cus ?? false;
  
  // CPA is "Waiting" when section not complete and CUS has pending actions
  const isWaitingForCus = isCpa && !sectionComplete && sectionNeedsAttention;

  return (
    <AccordionEditable 
      key="business-tax-situation" 
      title="Business Documents"
      hasRequiredActions={!isCpa && sectionNeedsAttention}
      allActionsComplete={sectionComplete}
      isWaiting={isWaitingForCus}
    >
      <div style={{ display: "flex", flexDirection: "column", gap: "20px" }}>
        {renderGainLossCard()}
        {renderStateAnnualReportCard()}
        {renderPurchaseAssetsCard()}
      </div>
    </AccordionEditable>
  );
};

export default BTFTaxSituationSection;
